import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledLink from 'Components/StyledLink'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  AffiliateOrderParamShape,
  AffiliateOrderShape
} from 'Services/SalesManagement/Features/Order/orderSlice'
import { ROWS_PER_PAGE_DEFAULT, SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import type { FC } from 'react'
import { t } from 'ttag'

const AffiliateOrderList: FC<{
  rows: AffiliateOrderShape[]
  loading: string
  params: AffiliateOrderParamShape
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  singleUrl: string
}> = ({ rows, loading, params, handleRequestSort, handleChangePage, count, singleUrl }) => {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/orders`
  const headCells: HeadCell[] = [
    {
      id: 'external_id',
      numeric: false,
      disablePadding: false,
      label: t`External ID`,
      sortable: false
    },
    {
      id: 'operator_name',
      numeric: false,
      disablePadding: false,
      label: t`Operator`,
      sortable: true
    },
    {
      id: 'sales_channel_name',
      numeric: false,
      disablePadding: false,
      label: t`Sales channel`,
      sortable: true
    },
    {
      id: 'waltti_id',
      numeric: false,
      disablePadding: false,
      label: t`Waltti ID`,
      sortable: false
    },
    {
      id: 'created',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    },
    {
      id: 'payment_method',
      numeric: false,
      disablePadding: false,
      label: t`Payment method`,
      sortable: false
    },
    {
      id: 'payment_status',
      numeric: false,
      disablePadding: false,
      label: t`Payment status`,
      sortable: false
    },
    {
      id: 'payment_cell_operator',
      numeric: false,
      disablePadding: false,
      label: t`Payment operator`,
      sortable: false
    },
    {
      id: 'order_value',
      numeric: false,
      disablePadding: false,
      label: t`Value`,
      sortable: false
    }
  ]

  const isLoading = loading === 'pending'

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={params.ordering}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow hover key={row.id}>
                    <NoWrapTableCell title={row.external_id}>
                      <StyledLink to={`${baseUrl}/${singleUrl}/${row.id}/`}>
                        {row.external_id}
                      </StyledLink>
                    </NoWrapTableCell>
                    <NoWrapTableCell title={row.operator_name}>{row.operator_name}</NoWrapTableCell>
                    <NoWrapTableCell title={row.sales_channel_name}>
                      {row.sales_channel_name}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.waltti_id)}>
                      {showValueOrHyphen(row.waltti_id)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={datetimeFormatter(row.created)}>
                      {datetimeFormatter(row.created)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.payment_method)}>
                      {showValueOrHyphen(row.payment_method)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.payment_status)}>
                      {showValueOrHyphen(row.payment_status)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.payment_cell_operator)}>
                      {showValueOrHyphen(row.payment_cell_operator)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.order_value)}>
                      {showValueOrHyphen(row.order_value, '€')}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={count}
          page={params.page - 1}
          onPageChange={handleChangePage}
        />
      </Paper>
    </WithLoader>
  )
}

export { AffiliateOrderList }
